import {
  LoadingMessageKeys,
  PropertyShowLoadingActions,
  PropertyShowLoadingStates,
  StageKeys,
  defaultInitialState,
} from './constants';
import { calculateProgress } from '../helpers/buildLoadingOverlayState';

/**
 * Adds a stage key to an array(completed) if not already present.
 * @param {string[]} stages - The current stages array.
 * @param {string} key - The stage key to add.
 * @returns {string[]} A new array with the key added, or the same array if already present.
 */
const addStageToCompleted = (stages, key) => (stages.includes(key) ? stages : [...stages, key]);

/**
 * Removes a stage key from an array(active).
 * @param {string[]} stages - The current stages array.
 * @param {string} key - The stage key to remove.
 * @returns {string[]} A new array without the key.
 */
const removeStageFromActive = (stages, key) => (stages || []).filter((s) => s !== key);

/**
 * The three data stages that run in parallel after meters are displayed.
 */
const PARALLEL_STAGES = [
  StageKeys.RULES,
  StageKeys.CONSUMED_DATA,
  StageKeys.GENERATED_DATA,
];

/**
 * Checks whether all three parallel data stages are present in completedStages.
 * Used as a gate to determine when the pipeline can transition to LOADING_TRADE_SUMMARY.
 * @param {string[]} completedStages - The current completed stages array.
 * @returns {boolean} True if RULES, CONSUMED_DATA, and GENERATED_DATA are all complete.
 */
const isParallelStagesCompleted = (completedStages = []) => (
  PARALLEL_STAGES.every((stage) => (completedStages).includes(stage))
);

/**
 * State machine reducer for the PropertyShow loading pipeline.
 * This reducer manages the state transitions and context updates for the
 * multi-stage property data fetching process. It tracks multiple concurrent
 * active stages via activeStages and completedStages arrays while maintaining
 * a backward-compatible state field. It is intentionally side-effect free to
 * simplify testing and reasoning about state changes.
 * @param {object} state - Current state of the machine
 * @param {object} action - Action to be performed
 * @param {string} action.type - Type of action to perform
 * @param {number} [action.metersCount] - Meter count for SET_METERS_LOADED
 * @param {number} [action.rulesCount] - Rules count for SET_RULES_LOADED
 * @param {number} [action.batchesLoaded] - Batches loaded for SET_TRADE_SUMMARY_PROGRESS
 * @param {number} [action.batchesTotal] - Total batches for SET_TRADE_SUMMARY_PROGRESS
 * @param {Error} [action.error] - Error for SET_ERROR
 * @returns {object} New state after applying the action
 */
const loadingStateReducer = (state, action) => {
  // Guard: when in a terminal state (CANCELLED or ERROR), only allow actions that
  // explicitly start a new request or reset. This prevents stale async dispatches
  // (e.g. from in-flight promises completing after cancellation) from overriding
  // the terminal state and causing the loading overlay to reappear and get stuck.
  const terminalStates = [
    PropertyShowLoadingStates.CANCELLED,
    PropertyShowLoadingStates.ERROR,
  ];
  const allowedInTerminalState = [
    PropertyShowLoadingActions.RESET,
    PropertyShowLoadingActions.START_REQUEST,
    PropertyShowLoadingActions.CANCEL_REQUEST,
    PropertyShowLoadingActions.HIDE_OVERLAY,
  ];
  if (terminalStates.includes(state.state)
    && !allowedInTerminalState.includes(action.type)) {
    return state;
  }

  switch (action.type) {
    case PropertyShowLoadingActions.RESET:
      return defaultInitialState();

    case PropertyShowLoadingActions.START_REQUEST: {
      const activeStages = [StageKeys.PROPERTY];
      const completedStages = [];
      return {
        state: PropertyShowLoadingStates.LOADING_PROPERTY,
        activeStages,
        completedStages,
        metadata: { ...defaultInitialState().metadata },
        overlay: {
          progressBarValue: calculateProgress(completedStages),
          messageKey: LoadingMessageKeys.LOADING_PROPERTY,
        },
      };
    }

    case PropertyShowLoadingActions.SET_PROPERTY_LOADED: {
      const activeStages = [StageKeys.METERS];
      const completedStages = addStageToCompleted(state.completedStages, StageKeys.PROPERTY);
      return {
        state: PropertyShowLoadingStates.LOADING_METERS,
        activeStages,
        completedStages,
        metadata: { ...state.metadata },
        overlay: {
          progressBarValue: calculateProgress(completedStages),
          messageKey: LoadingMessageKeys.PROPERTY_LOADED,
        },
      };
    }

    case PropertyShowLoadingActions.SET_METERS_LOADED: {
      const activeStages = [];
      const completedStages = addStageToCompleted(
        addStageToCompleted(state.completedStages, StageKeys.PROPERTY),
        StageKeys.METERS,
      );
      return {
        state: PropertyShowLoadingStates.METERS_LOADED,
        activeStages,
        completedStages,
        metadata: {
          ...state.metadata,
          metersCount: action.metersCount ?? state.metadata.metersCount,
        },
        overlay: {
          progressBarValue: calculateProgress(completedStages),
          messageKey: LoadingMessageKeys.METERS_LOADED,
        },
      };
    }

    case PropertyShowLoadingActions.METERS_DISPLAYED: {
      const activeStages = [StageKeys.RULES, StageKeys.CONSUMED_DATA, StageKeys.GENERATED_DATA];
      const completedStages = [...(state.completedStages || [])];
      return {
        state: PropertyShowLoadingStates.LOADING_RULES,
        activeStages,
        completedStages,
        metadata: { ...state.metadata },
        overlay: {
          progressBarValue: calculateProgress(completedStages),
          messageKey: LoadingMessageKeys.METERS_LOADED,
        },
      };
    }

    case PropertyShowLoadingActions.SET_RULES_LOADED: {
      const activeStages = removeStageFromActive(state.activeStages, StageKeys.RULES);
      const completedStages = addStageToCompleted(state.completedStages, StageKeys.RULES);
      const allDataLoaded = isParallelStagesCompleted(completedStages);
      return {
        state: allDataLoaded
          ? PropertyShowLoadingStates.LOADING_TRADE_SUMMARY
          : PropertyShowLoadingStates.RULES_LOADED,
        activeStages: allDataLoaded
          ? addStageToCompleted(activeStages, StageKeys.TRADE_SUMMARY)
          : activeStages,
        completedStages,
        metadata: {
          ...state.metadata,
          rulesCount: action.rulesCount ?? state.metadata.rulesCount,
        },
        overlay: {
          progressBarValue: calculateProgress(completedStages),
          messageKey: allDataLoaded
            ? LoadingMessageKeys.TRADE_SUMMARY_PROGRESS
            : LoadingMessageKeys.RULES_LOADED,
        },
      };
    }

    case PropertyShowLoadingActions.RULES_DISPLAYED: {
      if (state.state !== PropertyShowLoadingStates.RULES_LOADED) {
        return state;
      }
      const activeStages = removeStageFromActive(state.activeStages, StageKeys.RULES);
      const completedStages = addStageToCompleted(state.completedStages, StageKeys.RULES);
      return {
        state: PropertyShowLoadingStates.LOADING_CONSUMED,
        activeStages,
        completedStages,
        metadata: { ...state.metadata },
        overlay: {
          progressBarValue: calculateProgress(completedStages),
          messageKey: LoadingMessageKeys.RULES_LOADED,
        },
      };
    }

    case PropertyShowLoadingActions.SET_CONSUMED_DATA_LOADED: {
      const activeStages = removeStageFromActive(state.activeStages, StageKeys.CONSUMED_DATA);
      const completedStages = addStageToCompleted(state.completedStages, StageKeys.CONSUMED_DATA);
      const allDataLoaded = isParallelStagesCompleted(completedStages);
      let nextState = state.state;
      if (allDataLoaded) {
        nextState = PropertyShowLoadingStates.LOADING_TRADE_SUMMARY;
      } else if (state.state === PropertyShowLoadingStates.LOADING_CONSUMED) {
        nextState = PropertyShowLoadingStates.LOADING_GENERATED;
      }
      return {
        state: nextState,
        activeStages: allDataLoaded
          ? addStageToCompleted(activeStages, StageKeys.TRADE_SUMMARY)
          : activeStages,
        completedStages,
        metadata: { ...state.metadata },
        overlay: {
          progressBarValue: calculateProgress(completedStages),
          messageKey: allDataLoaded
            ? LoadingMessageKeys.TRADE_SUMMARY_PROGRESS
            : LoadingMessageKeys.CONSUMED_DATA_LOADED,
        },
      };
    }

    case PropertyShowLoadingActions.SET_GENERATED_DATA_LOADED: {
      const activeStages = removeStageFromActive(state.activeStages, StageKeys.GENERATED_DATA);
      const completedStages = addStageToCompleted(state.completedStages, StageKeys.GENERATED_DATA);
      const allDataLoaded = isParallelStagesCompleted(completedStages);
      return {
        state: allDataLoaded
          ? PropertyShowLoadingStates.LOADING_TRADE_SUMMARY
          : state.state,
        activeStages: allDataLoaded
          ? addStageToCompleted(activeStages, StageKeys.TRADE_SUMMARY)
          : activeStages,
        completedStages,
        metadata: { ...state.metadata },
        overlay: {
          progressBarValue: calculateProgress(completedStages),
          messageKey: allDataLoaded
            ? LoadingMessageKeys.TRADE_SUMMARY_PROGRESS
            : LoadingMessageKeys.CONSUMED_DATA_LOADED,
        },
      };
    }

    case PropertyShowLoadingActions.SET_TRADE_SUMMARY_PROGRESS:
      return {
        ...state,
        metadata: {
          ...state.metadata,
          batchesLoaded: action.batchesLoaded ?? state.metadata.batchesLoaded,
          batchesTotal: action.batchesTotal ?? state.metadata.batchesTotal,
        },
        overlay: {
          ...state.overlay,
          progressBarValue: calculateProgress(state.completedStages),
          messageKey: LoadingMessageKeys.TRADE_SUMMARY_PROGRESS,
        },
      };

    case PropertyShowLoadingActions.MARK_FULLY_LOADED: {
      const activeStages = [StageKeys.DATA_PREPARED];
      // Mark all stages up to and including TRADE_SUMMARY as completed.
      // This handles the case where intermediate dispatches were skipped
      // (e.g., empty meters causing an early return in data fetching).
      const allStagesUpToTradeSummary = [
        StageKeys.PROPERTY,
        StageKeys.METERS,
        StageKeys.RULES,
        StageKeys.CONSUMED_DATA,
        StageKeys.GENERATED_DATA,
        StageKeys.TRADE_SUMMARY,
      ];
      const completedStages = allStagesUpToTradeSummary.reduce(
        (stages, stage) => addStageToCompleted(stages, stage),
        state.completedStages || [],
      );
      return {
        state: PropertyShowLoadingStates.PREPARING_DATA,
        activeStages,
        completedStages,
        // Cache hits skip intermediate stage dispatches (SET_METERS_LOADED, SET_RULES_LOADED,
        // SET_TRADE_SUMMARY_PROGRESS), so the hook computes and passes metadata from the cached
        // segment. Network fetches omit these fields because earlier stages already populated
        // state.metadata. The ?? operator ensures both paths produce correct metadata.
        metadata: {
          ...state.metadata,
          metersCount: action.metersCount ?? state.metadata.metersCount,
          rulesCount: action.rulesCount ?? state.metadata.rulesCount,
          batchesLoaded: action.batchesLoaded ?? state.metadata.batchesLoaded,
          batchesTotal: action.batchesTotal ?? state.metadata.batchesTotal,
        },
        overlay: {
          progressBarValue: calculateProgress(completedStages),
          messageKey: LoadingMessageKeys.PREPARING_DATA,
        },
      };
    }

    case PropertyShowLoadingActions.DATA_PREPARED: {
      if (state.state !== PropertyShowLoadingStates.PREPARING_DATA) {
        return state;
      }
      const activeStages = [StageKeys.DATA_RENDERED];
      const completedStages = addStageToCompleted(state.completedStages, StageKeys.DATA_PREPARED);
      return {
        state: PropertyShowLoadingStates.RENDERING,
        activeStages,
        completedStages,
        metadata: { ...state.metadata },
        overlay: {
          progressBarValue: calculateProgress(completedStages),
          messageKey: LoadingMessageKeys.RENDERING,
        },
      };
    }

    case PropertyShowLoadingActions.RENDERING_DISPLAYED: {
      if (state.state !== PropertyShowLoadingStates.RENDERING) {
        return state;
      }
      const activeStages = [];
      const completedStages = addStageToCompleted(state.completedStages, StageKeys.DATA_RENDERED);
      return {
        state: PropertyShowLoadingStates.COMPLETE,
        activeStages,
        completedStages,
        metadata: { ...state.metadata },
        overlay: {
          progressBarValue: calculateProgress(completedStages),
          messageKey: LoadingMessageKeys.COMPLETE,
        },
      };
    }

    case PropertyShowLoadingActions.HIDE_OVERLAY:
      if (state.state === PropertyShowLoadingStates.ERROR) {
        return state;
      }
      return {
        ...state,
        state: PropertyShowLoadingStates.IDLE,
        activeStages: [],
        completedStages: [],
        overlay: {
          progressBarValue: 0,
          messageKey: LoadingMessageKeys.NONE,
        },
      };

    case PropertyShowLoadingActions.SET_ERROR:
      return {
        state: PropertyShowLoadingStates.ERROR,
        activeStages: [],
        completedStages: [...(state.completedStages || [])],
        metadata: {
          ...state.metadata,
          error: action.error || null,
        },
        overlay: {
          progressBarValue: 0,
          messageKey: LoadingMessageKeys.NONE,
        },
      };

    case PropertyShowLoadingActions.CANCEL_REQUEST:
      return {
        state: PropertyShowLoadingStates.CANCELLED,
        activeStages: [],
        completedStages: [...(state.completedStages || [])],
        metadata: { ...state.metadata },
        overlay: {
          progressBarValue: 0,
          messageKey: LoadingMessageKeys.NONE,
        },
      };

    default:
      return state;
  }
};

export default loadingStateReducer;
